import React, { useId } from 'react';
import { SvgIcon, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { ReactComponent as PersonWalkIcon } from 'assets/icons/PersonWalkIcon.svg';
import { useThemeUtils } from 'contexts/ThemeContext/hooks/useThemeUtils';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { TaskCard, TaskCardProps } from '../TaskCard/TaskCard';
import { ProgressBar } from '../ProgressBar/ProgressBar';

interface StepCountCardProps extends Omit<TaskCardProps, 'taskTypeLabel' | 'icon'> {
  stepsProgress: number;
  recordedSteps: number;
}
/**
 * StepCountCard component displays a task card with a step count progress bar.
 *
 * @param {boolean} isCompleted - Indicates if the task is completed.
 * @param {() => void} onClick - Callback function to handle click events.
 * @param {number} stepsProgress - The current step count value for the progress bar.
 *
 * @returns {JSX.Element} The rendered StepCountCard component.
 */
export const StepCountCard = ({
  isCompleted,
  onClick,
  stepsProgress,
  recordedSteps,
}: StepCountCardProps) => {
  const { theme } = useThemeUtils();
  const { translation } = useTranslationContext();
  const recordedStepsId = useId();

  return (
    <TaskCard
      icon={<SvgIcon component={PersonWalkIcon} />}
      taskTypeLabel={translation('step_count_settings')}
      taskTypeLabelColor={theme.palette.grey[900]}
      isCompleted={isCompleted}
      onClick={onClick}
      taskCardLabeledby={recordedStepsId}
    >
      <Typography id={recordedStepsId} sx={visuallyHidden}>
        {recordedSteps}
      </Typography>
      <ProgressBar value={stepsProgress} id="step-count-progress-bar" aria-hidden />
    </TaskCard>
  );
};
